import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Compass, Download, GraduationCap, Sparkles } from "lucide-react";
import CourseCard from "@/components/CourseCard";
import { CoachReco } from "@/components/CoachReco";
import { MemberProfileCard } from "@/components/MemberProfileCard";

export default function DashboardPage() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    http.get("/me/enrollments").then((r) => setEnrollments(r.data));
    http.get("/formations").then((r) => setSuggestions(r.data));
  }, []);

  const { toDiscover, completedEnrollments } = useMemo(() => {
    const enrolledSlugs = new Set(enrollments.map((e) => e.formation.slug));
    return {
      toDiscover: suggestions.filter((f) => !enrolledSlugs.has(f.slug)).slice(0, 3),
      completedEnrollments: enrollments.filter((e) => e.progress === 100),
    };
  }, [enrollments, suggestions]);

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-8 container-x py-10 min-h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-24">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3 pl-3">Espace membre</div>
          <nav className="space-y-1">
            {[
              { icon: Compass, label: "Tableau de bord", to: "/espace", active: true },
              { icon: BookOpen, label: "Mes formations", to: "/espace" },
              { icon: GraduationCap, label: "Certificats", to: "/espace" },
              { icon: Sparkles, label: "Ressources", to: "/blog" },
            ].map((n, i) => (
              <Link key={n.label} to={n.to} data-testid={`side-${i}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${n.active ? "bg-brand-50 text-brand-800" : "text-slate-700 hover:bg-slate-100"}`}>
                <n.icon size={16} strokeWidth={1.5} /> {n.label}
              </Link>
            ))}
          </nav>
        </div>
      </aside>

      {/* Content */}
      <div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2">Bienvenue</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">Bonjour {user?.name || "vous"} 👋</h1>
            <p className="text-slate-600 mt-2">Reprenez là où vous vous étiez arrêté(e).</p>
          </div>
          <div className="flex gap-4">
            <div className="card-soft px-5 py-4 min-w-[140px]">
              <div className="text-xs uppercase tracking-widest text-slate-500">Formations</div>
              <div className="font-display text-2xl font-bold text-slate-900">{enrollments.length}</div>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <CoachReco formations={suggestions} />
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-bold text-slate-900">Mes formations en cours</h2>
            <Link to="/formations" className="text-sm text-brand-700 hover:text-brand-800 font-semibold">Découvrir plus</Link>
          </div>
          {enrollments.length === 0 ? (
            <div className="card-soft p-10 text-center">
              <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-100 text-brand-700 mx-auto mb-4"><BookOpen strokeWidth={1.5} /></div>
              <p className="text-slate-700 font-medium">Vous n'avez pas encore commandé de formation.</p>
              <p className="text-slate-500 text-sm mt-1">Avec votre profil, vous pouvez :</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-600 max-w-sm mx-auto text-left">
                <li>• Commander une formation et recevoir un devis personnalisé</li>
                <li>• Télécharger vos attestations de participation</li>
                <li>• Accéder aux guides de référence traduits en français</li>
              </ul>
              <Link to="/formations" className="btn-primary mt-6 inline-flex">Voir les formations</Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {enrollments.map((e) => (
                <CourseCard key={e.formation.slug} f={e.formation} linkTo={`/espace/formation/${e.formation.slug}`} />
              ))}
            </div>
          )}
        </div>

        {toDiscover.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold text-slate-900">Formations à découvrir</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {toDiscover.map((f) => <CourseCard key={f.slug} f={f} />)}
            </div>
          </div>
        )}

        {completedEnrollments.length > 0 && (
          <div className="mt-14" data-testid="attestations-section">
            <h2 className="font-display text-xl font-bold text-slate-900 mb-5">Mes attestations de participation</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {completedEnrollments.map((e) => (
                <div key={e.formation.slug} className="card-soft p-6 flex items-center gap-4" data-testid={`attestation-card-${e.formation.slug}`}>
                  <div className="grid place-items-center h-12 w-12 rounded-2xl bg-yellow-400/20 text-yellow-600 shrink-0"><GraduationCap strokeWidth={1.5} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm leading-snug">{e.formation.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Session complétée à 100 %</div>
                  </div>
                  <a
                    href={`${API}/me/attestations/${e.formation.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`attestation-download-${e.formation.slug}`}
                    className="btn-primary text-xs shrink-0"
                  >
                    <Download size={14} strokeWidth={2} /> Télécharger
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <MemberProfileCard />
      </div>
    </div>
  );
}
